import type { AnchorCategoryUpdate, AssetService } from '../types';
import { SupabaseAssetInstance, isAnchorCategory } from '@/types/assetTypes';
import { createAssetInstance } from '@/utils/dataConverters';
import { ClipServiceImpl } from '../clips';

import type { WriteScope } from '@/modules/undoRedo/UndoStack';
import type { MediaClipOverrides } from '@/types/clipTypes';
import { untracked } from '@/modules/undoRedo/sceneWrites';
export class AssetServiceImpl {
  public sceneService?: any; // SceneService type will be injected from services layer

  constructor(
    private readonly assetService: AssetService,
    private readonly clipService: ClipServiceImpl,
  ) { }

  async fetchAssetTypes(type?: string) {
    return this.assetService.fetchAssetTypes(type);
  }

  async getAssetTypeIdBySourceNameAndCategory(sourceName: string, category: string) {
    return this.assetService.getAssetTypeIdBySourceNameAndCategory(sourceName, category);
  }

  async getAssetTypeBySourceNameAndCategory(sourceName: string, category: string) {
    return this.assetService.getAssetTypeBySourceNameAndCategory(sourceName, category);
  }

  async getCameraAssetType() {
    return this.assetService.getCameraAssetType();
  }

  async fetchTriggerAssetType() {
    return this.assetService.fetchTriggerAssetType();
  }

  async fetchTriggerAssetTypes() {
    return this.assetService.fetchTriggerAssetTypes();
  }

  async fetchTriggerInstancesByScene(sceneInstanceId: string) {
    return this.assetService.fetchTriggerInstancesByScene(sceneInstanceId);
  }

  async fetchAssetTypeRow(assetTypeId: string) {
    return this.assetService.fetchAssetTypeRow(assetTypeId);
  }

  async fetchAssetInstanceRow(assetInstanceId: string) {
    return this.assetService.fetchAssetInstanceRow(assetInstanceId);
  }

  /** The scene's anchor category after an asset of `category` is added or removed; no other asset can change it. */
  private async anchorsChanged(sceneInstanceId: string, scope?: WriteScope, category?: string | null) {
    if (!isAnchorCategory(category)) return;
    if (!this.sceneService) {
      console.warn('⚠️ [AssetServiceImpl] No scene service available for anchor category update');
      return;
    }
    try {
      await this.sceneService.updateSceneAnchorCategory(sceneInstanceId, scope);
    } catch (error) {
      console.warn('⚠️ [AssetServiceImpl] Failed to update scene anchor category:', error);
    }
  }

  async addAssetInstanceRow(newAssetInstance: SupabaseAssetInstance, scope?: WriteScope) {
    const result = await this.assetService.addAssetInstanceRow(newAssetInstance, scope);
    if (newAssetInstance.scene_instance_id) {
      await this.anchorsChanged(newAssetInstance.scene_instance_id, scope, newAssetInstance.asset_instance_category);
    }
    return result;
  }

  async deleteAssetInstanceRow(assetId: string, alsoOnDeleted?: AnchorCategoryUpdate, scope?: WriteScope) {
    const onAssetDeleted: AnchorCategoryUpdate = async (sceneInstanceId, deleteScope, category) => {
      await this.anchorsChanged(sceneInstanceId, deleteScope, category);
      await alsoOnDeleted?.(sceneInstanceId, deleteScope, category);
    };
    return this.assetService.deleteAssetInstanceRow(assetId, onAssetDeleted, scope);
  }

  async deleteAssetType(assetTypeId: string, scope?: WriteScope) {
    await this.assetService.deleteAssetType(assetTypeId, scope);
  }

  async updateAssetTypeDisplayName(assetTypeId: string, newDisplayName: string, scope?: WriteScope) {
    return this.assetService.updateAssetTypeDisplayName(assetTypeId, newDisplayName, scope);
  }

  async addInitialCameraToScene(userId: string, sceneInstanceId: string) {
    try {
      // Get camera asset type data in a single optimized query
      // This combines ID lookup + row fetch into one query (instead of two separate queries)
      const cameraAssetType = await this.assetService.getCameraAssetType();

      // Use the existing createAssetInstance helper
      const newCameraInstance = createAssetInstance(userId, sceneInstanceId, cameraAssetType);
      // Explicitly set camera to inactive
      newCameraInstance.asset_instance_is_active = false;

      // Add camera to database (scene anchor update is skipped for camera assets); every scene has one, so it is not an undo step
      const newCameraData = await this.assetService.addAssetInstanceRow(newCameraInstance, untracked);

      // Create default keyframes for camera - this is mandatory
      // Camera guy must have keyframes for proper timeline functionality
      const assetAdapter = this.assetService as any;
      await assetAdapter['createDefaultKeyframesForAsset'](newCameraData[0], untracked);

      return newCameraData[0];
    } catch (error) {
      console.error('Error adding initial camera to scene:', error);
      throw error;
    }
  }

  /**
   * Ensures that a scene has a camera asset instance with keyframes.
   * If the scene doesn't have a camera, this will create one.
   * This is a safety net for older scenes that may have been created without cameras.
   */
  async ensureSceneHasCamera(userId: string, sceneInstanceId: string): Promise<boolean> {
    try {
      // Check if scene already has a camera
      const assets = await this.assetService.fetchAllSceneAssets(sceneInstanceId);
      const cameraAsset = assets.find((asset: any) => asset.asset_instance_category === 'camera');

      if (cameraAsset) {
        // Camera exists - no action needed
        return false;
      }

      // No camera found - create one
      console.warn(`⚠️ Scene ${sceneInstanceId} is missing a camera asset. Creating one now...`);
      await this.addInitialCameraToScene(userId, sceneInstanceId);
      console.log(`✅ Camera asset created for scene ${sceneInstanceId}`);
      return true;

    } catch (error) {
      console.error('Error ensuring scene has camera:', error);
      // Don't throw - this is a validation/recovery function
      return false;
    }
  }

  async updateAssetInstanceDetails(assetInstanceId: string, details: Record<string, unknown>, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceDetails(assetInstanceId, details, scope);
  }

  async setAssetInstanceDetailFlag(assetInstanceId: string, key: string, value: boolean, scope?: WriteScope) {
    return this.assetService.setAssetInstanceDetailFlag(assetInstanceId, key, value, scope);
  }

  async updateAssetInstanceDisplayName(assetInstanceId: string, displayName: string, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceDisplayName(assetInstanceId, displayName, scope);
  }

  async updateAssetInstanceRowPosition(assetInstanceId: string, beginTime: number, endTime: number, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceRowPosition(assetInstanceId, beginTime, endTime, scope);
  }

  async updateAssetInstanceEndTime(assetInstanceId: string, endTime: number, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceEndTime(assetInstanceId, endTime, scope);
  }

  async updateAssetInstanceLockState(assetInstanceId: string, isLocked: boolean, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceLockState(assetInstanceId, isLocked, scope);
  }

  async updateAssetInstanceActiveState(instanceId: string, isActive: boolean, scope?: WriteScope) {
    return this.assetService.updateAssetInstanceActiveState(instanceId, isActive, scope);
  }

  async batchUpdateAssetInstanceDisplayOrders(updates: Array<{ assetInstanceId: string; displayOrder: number | null }>, scope?: WriteScope) {
    return this.assetService.batchUpdateAssetInstanceDisplayOrders(updates, scope);
  }

  async fetchAllSceneAssets(sceneId: string) {
    return this.assetService.fetchAllSceneAssets(sceneId);
  }

  async addAssetToScene(
    asset: { typeId: string; type?: string; typeDisplayName: string },
    userId: string,
    sceneInstanceId: string,
    environmentPreferences?: { keyframes_on?: boolean },
    onSceneAnchorCategoryUpdate?: AnchorCategoryUpdate,
    assetTypeData?: any, // Optional: if provided, skip the fetch (optimization)
    instanceOverrides?: Partial<SupabaseAssetInstance>,
    mediaClipOverrides?: MediaClipOverrides,
    scope?: WriteScope
  ) {
    console.log(`🔍 [AssetServiceImpl] addAssetToScene called for asset type: ${asset.type}, scene: ${sceneInstanceId}`);

    const updateCallback: AnchorCategoryUpdate = onSceneAnchorCategoryUpdate ?? ((sceneId, anchorScope, category) => this.anchorsChanged(sceneId, anchorScope, category));

    return this.assetService.addAssetToScene(
      asset,
      userId,
      sceneInstanceId,
      environmentPreferences,
      updateCallback,
      assetTypeData,
      instanceOverrides,
      mediaClipOverrides,
      scope
    );
  }

  // This function is no longer needed - clips are managed by Redux
}